/**
 * The annotator's view of the vocabulary (§4.4, §9.1 `vocabStore`).
 *
 * Every kind the stage editor offers — shapes (`region_kind`), spots (`anchor_kind`), ways in and
 * out (`portal_kind`) and paths (`path_kind`) — comes from the live registry in
 * `src/stores/vocabStore.ts`. The editor codes against the tiny {@link VocabLike} interface below
 * and falls back to a short built-in table ONLY while the registry has not loaded or failed to (a
 * fresh checkout, a unit test, the request failing), so it is never unusable because the
 * vocabulary is late.
 *
 * Lookups are **domain-scoped**: `door` is both a spot and a way in/out, `walk` both a path and an
 * action, and each has its own colour, label and fields. Callers that know the domain pass it.
 *
 * The second half of the file holds the kind edits the panels share (change the kind of what is
 * selected, write a per-kind field, add a way in/out). They go through the editor store's public
 * gesture API so each is exactly one undo step.
 */
import { nanoid } from 'nanoid'
import { useMemo, useSyncExternalStore } from 'react'

import { humanProvenance, isStageBody, useEditorStore, type EditorDoc } from '@/stores/editorStore'
import {
  digitKeys,
  isStageDomain,
  kindsOf,
  resolveKind,
  STAGE_DOMAINS,
  useVocabStore,
  type DigitKey,
  type StageDomain,
  type VocabState,
} from '@/stores/vocabStore'
import type { Domain, KindDef, PortalEdge, StageBody } from '@/schema'

import type { ToolId } from './tools/types'

export { STAGE_DOMAINS, isStageDomain, type StageDomain }

export interface VocabLike {
  byDomain(domain: Domain): KindDef[]
  /** Domain-scoped when `domain` is given; otherwise the first stage domain that knows the id. */
  get(id: string, domain?: Domain): KindDef | undefined
  rev?: number
}

/* -------------------------------------------------------------------------- */
/* Built-in fallback (only while the registry is missing)                      */
/* -------------------------------------------------------------------------- */

type RawKind = { id: string; label: string; color: string; icon: string; semantics: Record<string, unknown> }

/** The seed's builtins the stage editor cannot work without, in `sort_order`. Icons are kebab-case like the registry's. */
const FALLBACK_REGION_KINDS: RawKind[] = [
  { id: 'sky', label: 'Sky', color: 'oklch(0.86 0.09 235)', icon: 'cloud', semantics: { roles: ['backdrop', 'fly'], z_order: 10, surface: 'sky' } },
  { id: 'ground', label: 'Ground', color: 'oklch(0.74 0.13 140)', icon: 'footprints', semantics: { roles: ['walk', 'support'], z_order: 20, surface: 'floor' } },
  { id: 'water', label: 'Water', color: 'oklch(0.72 0.14 225)', icon: 'waves', semantics: { roles: ['swim', 'support'], z_order: 30, surface: 'water' } },
  { id: 'wall', label: 'Wall', color: 'oklch(0.78 0.04 60)', icon: 'square', semantics: { roles: ['backdrop', 'block', 'support'], z_order: 40 } },
  { id: 'obstacle', label: 'Obstacle', color: 'oklch(0.62 0.16 25)', icon: 'octagon-x', semantics: { roles: ['block'], z_order: 40 } },
  { id: 'walk_behind', label: 'Walk behind', color: 'oklch(0.68 0.12 300)', icon: 'eye-off', semantics: { roles: ['occlude'], z_order: 50, requires: ['baseline_y'] } },
  { id: 'hide_spot', label: 'Hiding place', color: 'oklch(0.64 0.12 320)', icon: 'venetian-mask', semantics: { roles: ['hide', 'occlude'], z_order: 50, requires: ['baseline_y'] } },
  { id: 'platform', label: 'Platform', color: 'oklch(0.76 0.11 105)', icon: 'layout-panel-top', semantics: { roles: ['walk', 'support'], z_order: 20, requires: ['elevation'] } },
  { id: 'scenery', label: 'Scenery', color: 'oklch(0.80 0.06 160)', icon: 'mountain', semantics: { roles: ['backdrop'], z_order: 10 } },
]

const SPOT = { stand_offset: [0, 3], facing: 'camera' }
const FALLBACK_ANCHOR_KINDS: RawKind[] = [
  { id: 'door', label: 'Door', color: 'oklch(0.74 0.13 70)', icon: 'door-open', semantics: { ...SPOT, affords: ['enter', 'exit'] } },
  { id: 'bed', label: 'Bed', color: 'oklch(0.74 0.13 70)', icon: 'bed', semantics: { ...SPOT, affords: ['sleep', 'sit'] } },
  { id: 'chair', label: 'Chair', color: 'oklch(0.74 0.13 70)', icon: 'armchair', semantics: { ...SPOT, affords: ['sit', 'sit_at'] } },
  { id: 'table', label: 'Table', color: 'oklch(0.74 0.13 70)', icon: 'table', semantics: { ...SPOT, affords: ['sit_at', 'eat', 'place'] } },
  { id: 'tree', label: 'Tree', color: 'oklch(0.74 0.13 70)', icon: 'tree-pine', semantics: { ...SPOT, affords: ['hide', 'climb'] } },
  { id: 'spawn', label: 'Start spot', color: 'oklch(0.74 0.13 70)', icon: 'map-pin', semantics: { ...SPOT, affords: [] } },
]

const FALLBACK_PORTAL_KINDS: RawKind[] = [
  { id: 'edge', label: 'Edge of the picture', color: 'oklch(0.70 0.15 300)', icon: 'move-horizontal', semantics: { transition: 'walk_off', needs_anchor: false } },
  { id: 'door', label: 'Door', color: 'oklch(0.70 0.15 300)', icon: 'door-open', semantics: { transition: 'door_open', needs_anchor: true } },
  { id: 'path', label: 'Path', color: 'oklch(0.70 0.15 300)', icon: 'route', semantics: { transition: 'walk_off', needs_anchor: false } },
  { id: 'stairs', label: 'Stairs', color: 'oklch(0.70 0.15 300)', icon: 'chevrons-up', semantics: { transition: 'walk_off', needs_anchor: true } },
  { id: 'water_edge', label: "Water's edge", color: 'oklch(0.70 0.15 300)', icon: 'waves', semantics: { transition: 'fade', needs_anchor: false } },
  { id: 'magic', label: 'Magic', color: 'oklch(0.70 0.15 300)', icon: 'sparkles', semantics: { transition: 'fade', needs_anchor: false } },
]

const FALLBACK_PATH_KINDS: RawKind[] = [
  { id: 'walk', label: 'Walking path', color: 'oklch(0.74 0.13 140)', icon: 'footprints', semantics: { locomotion: 'walk' } },
  { id: 'fly', label: 'Flying path', color: 'oklch(0.86 0.09 235)', icon: 'bird', semantics: { locomotion: 'fly' } },
  { id: 'swim', label: 'Swimming path', color: 'oklch(0.72 0.14 225)', icon: 'waves', semantics: { locomotion: 'swim' } },
  { id: 'climb', label: 'Climbing path', color: 'oklch(0.76 0.11 105)', icon: 'move-up', semantics: { locomotion: 'climb' } },
]

function mk(domain: Domain, raw: RawKind, order: number): KindDef {
  return {
    id: raw.id,
    domain,
    label: { en: raw.label },
    color: raw.color,
    icon: raw.icon,
    applies_to: ['stage'],
    semantics: { domain, ...raw.semantics } as unknown as KindDef['semantics'],
    fields: [],
    origin: 'builtin',
    enabled: true,
    deprecated: false,
    sort_order: order,
  } as KindDef
}

const FALLBACK: Partial<Record<Domain, KindDef[]>> = {
  region_kind: FALLBACK_REGION_KINDS.map((r, i) => mk('region_kind', r, i)),
  anchor_kind: FALLBACK_ANCHOR_KINDS.map((r, i) => mk('anchor_kind', r, i)),
  portal_kind: FALLBACK_PORTAL_KINDS.map((r, i) => mk('portal_kind', r, i)),
  path_kind: FALLBACK_PATH_KINDS.map((r, i) => mk('path_kind', r, i)),
}

/** Every fallback id, per domain — the test checks each exists in the seed. */
export const FALLBACK_IDS: Readonly<Record<StageDomain, readonly string[]>> = {
  region_kind: FALLBACK_REGION_KINDS.map((k) => k.id),
  anchor_kind: FALLBACK_ANCHOR_KINDS.map((k) => k.id),
  portal_kind: FALLBACK_PORTAL_KINDS.map((k) => k.id),
  path_kind: FALLBACK_PATH_KINDS.map((k) => k.id),
}

export const fallbackVocab: VocabLike = {
  byDomain: (domain) => FALLBACK[domain] ?? [],
  get: (id, domain) => {
    if (domain) return FALLBACK[domain]?.find((k) => k.id === id)
    for (const d of STAGE_DOMAINS) {
      const hit = FALLBACK[d]?.find((k) => k.id === id)
      if (hit) return hit
    }
    return undefined
  },
  rev: 0,
}

/* -------------------------------------------------------------------------- */
/* Colour, label, description                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Deterministic colour for a kind the vocabulary does not know (`x:lava` from an old document,
 * or a disabled builtin). Same id → same hue, every session.
 */
export function colourForUnknownKind(id: string): string {
  let h = 0
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0
  return `oklch(0.70 0.12 ${h % 360})`
}

/** Colour of a kind, falling back to a stable hash and finally to grey. */
export function kindColour(vocab: VocabLike, id: string | undefined, domain?: Domain): string {
  if (!id) return 'oklch(0.65 0.02 250)'
  const def = vocab.get(id, domain)
  if (def?.color) return def.color
  return colourForUnknownKind(id)
}

/** `x:floor_table_bay` → "floor table bay". */
export function prettyId(id: string): string {
  return id.replace(/^x:/, '').replace(/_/g, ' ')
}

/** English label for a kind id, falling back to the id itself. */
export function kindLabel(vocab: VocabLike, id: string | undefined, domain?: Domain): string {
  if (!id) return 'No kind'
  const def = vocab.get(id, domain)
  return def?.label['en'] ?? prettyId(id)
}

/** "shape" / "spot" / "way in or out" / "path" — the words a parent reads. */
export const DOMAIN_WORDS: Readonly<Record<StageDomain, { one: string; many: string; title: string }>> = {
  region_kind: { one: 'shape', many: 'shapes', title: 'Shapes' },
  anchor_kind: { one: 'spot', many: 'spots', title: 'Spots' },
  portal_kind: { one: 'way in or out', many: 'ways in and out', title: 'Ways in and out' },
  path_kind: { one: 'path', many: 'paths', title: 'Paths' },
}

const ROLE_SENTENCE: Record<string, string> = {
  walk: 'Characters walk on it.',
  swim: 'Characters swim in it.',
  fly: 'Flying characters move through it.',
  block: 'Nobody can walk through it.',
  hide: 'Characters can hide behind it.',
  occlude: 'It is drawn in front of characters behind it.',
  backdrop: 'It is background.',
  support: 'Things can rest on it.',
}

const ROLE_ORDER = ['walk', 'swim', 'fly', 'block', 'hide', 'occlude', 'support', 'backdrop']

const AFFORD_SENTENCE: Record<string, string> = {
  sit: 'Characters can sit on it.',
  sit_at: 'Characters can sit at it.',
  sleep: 'Characters can sleep in it.',
  eat: 'Characters can eat here.',
  hide: 'Characters can hide behind it.',
  climb: 'Characters can climb it.',
  look_out: 'Characters can look out of it or at it.',
  place: 'Things can be put on it.',
  enter: 'Characters can come in here.',
  exit: 'Characters can go out here.',
  swim: 'Characters can swim here.',
}

const TRANSITION_SENTENCE: Record<string, string> = {
  walk_off: 'Characters walk out of the picture here.',
  door_open: 'It opens like a door to the next place.',
  fade: 'The picture fades to the next place.',
  cut: 'The story cuts straight to the next place.',
}

const LOCOMOTION_SENTENCE: Record<string, string> = {
  walk: 'Characters walk along it.',
  fly: 'Characters fly along it.',
  swim: 'Characters swim along it.',
  climb: 'Characters climb along it.',
}

/** Plain sentences for what a kind does in stories (the offline twin of the server's summary). */
export function describeKind(kind: KindDef | undefined): string[] {
  if (!kind) return []
  const s = kind.semantics as unknown as Record<string, unknown>
  const list = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [])
  switch (kind.domain) {
    case 'region_kind': {
      const roles = list(s['roles'])
      if (roles.length === 0) return ['It does nothing in stories yet.']
      // Most telling first (a wall "blocks the way" before it "is background").
      const out = [...roles]
        .sort((a, b) => ROLE_ORDER.indexOf(a) - ROLE_ORDER.indexOf(b))
        .map((r) => ROLE_SENTENCE[r])
        .filter((x): x is string => Boolean(x))
      if (list(s['requires']).includes('baseline_y')) out.push('It needs a ground line.')
      if (list(s['requires']).includes('elevation')) out.push('It needs a height.')
      return [...new Set(out)]
    }
    case 'anchor_kind': {
      const affords = list(s['affords'])
      if (affords.length === 0) return ['A marker: characters can start or stand here.']
      const out = [...new Set(affords.map((a) => AFFORD_SENTENCE[a]).filter((x): x is string => Boolean(x)))]
      return out.length ? out : [`Characters can ${affords.join(', ').replace(/_/g, ' ')} here.`]
    }
    case 'portal_kind': {
      const out = [TRANSITION_SENTENCE[String(s['transition'])] ?? 'It leads to another place.']
      if (s['needs_anchor'] === true) out.push('It sits on a spot, like a door.')
      return out
    }
    case 'path_kind':
      return [LOCOMOTION_SENTENCE[String(s['locomotion'])] ?? 'Characters move along it.']
    default:
      return kind.description ? [kind.description] : []
  }
}

/* -------------------------------------------------------------------------- */
/* Domains ↔ tools ↔ features                                                  */
/* -------------------------------------------------------------------------- */

export type KindSlot = 'region' | 'anchor' | 'portal' | 'path'

export const SLOT_OF: Readonly<Record<StageDomain, KindSlot>> = {
  region_kind: 'region',
  anchor_kind: 'anchor',
  portal_kind: 'portal',
  path_kind: 'path',
}

/** Which kind list a drawing tool uses; `null` for tools that draw no kind (select, horizon, zoom…). */
export function domainOfTool(tool: ToolId): StageDomain | null {
  switch (tool) {
    case 'polygon':
    case 'band':
    case 'rect':
      return 'region_kind'
    case 'anchor':
      return 'anchor_kind'
    case 'portal':
      return 'portal_kind'
    case 'path':
      return 'path_kind'
    default:
      return null
  }
}

/** The tool that adds a new feature of `domain`. */
export function toolOfDomain(domain: StageDomain): ToolId {
  switch (domain) {
    case 'region_kind':
      return 'polygon'
    case 'anchor_kind':
      return 'anchor'
    case 'portal_kind':
      return 'portal'
    case 'path_kind':
      return 'path'
  }
}

type Kinded = StageBody['regions'][number] | StageBody['anchors'][number] | StageBody['portals'][number] | StageBody['paths'][number]

/** The features of one domain in a stage document. */
export function featuresOf(doc: EditorDoc | null, domain: StageDomain): Kinded[] {
  if (!isStageBody(doc)) return []
  switch (domain) {
    case 'region_kind':
      return doc.regions
    case 'anchor_kind':
      return doc.anchors
    case 'portal_kind':
      return doc.portals
    case 'path_kind':
      return doc.paths
  }
}

/** Which kind list feature `id` belongs to (`null` for layers and unknown ids). */
export function domainOfFeature(doc: EditorDoc | null, id: string | undefined): StageDomain | null {
  if (!id || !isStageBody(doc)) return null
  for (const d of STAGE_DOMAINS) if (featuresOf(doc, d).some((f) => f.id === id)) return d
  return null
}

/** The one domain every selected feature shares, or `null` (nothing selected, or a mix). */
export function domainOfSelection(doc: EditorDoc | null, ids: readonly string[]): StageDomain | null {
  let found: StageDomain | null = null
  for (const id of ids) {
    const d = domainOfFeature(doc, id)
    if (!d) continue
    if (found && found !== d) return null
    found = d
  }
  return found
}

/** How many features of each kind the document has, for "On this picture" and the chip counts. */
export function kindCounts(doc: EditorDoc | null, domain: StageDomain): Map<string, number> {
  const counts = new Map<string, number>()
  for (const f of featuresOf(doc, domain)) counts.set(f.kind, (counts.get(f.kind) ?? 0) + 1)
  return counts
}

/**
 * The `1–9` keys of a domain: pins first, then the registry's own digit shortcuts, then
 * `sort_order` (see `digitKeys`). The same list the kind bar shows and the digits drive.
 */
export function digitShortcuts(vocab: VocabLike, domain: Domain, pins: readonly string[] = []): DigitKey[] {
  return digitKeys(vocab.byDomain(domain), pins)
}

/**
 * A sensible kind to arm for `domain` before the person picks one: ground-like for shapes (a walk
 * role), a way that needs no spot for ways in and out, walking for paths, else the first key.
 */
export function defaultKind(vocab: VocabLike, domain: StageDomain): string | undefined {
  const rows = vocab.byDomain(domain).filter((k) => k.enabled && !k.deprecated)
  const sem = (k: KindDef) => k.semantics as unknown as Record<string, unknown>
  const pick = (test: (k: KindDef) => boolean) => rows.find(test)?.id
  switch (domain) {
    case 'region_kind':
      return pick((k) => k.id === 'ground') ?? pick((k) => (sem(k)['roles'] as string[] | undefined)?.includes('walk') === true) ?? rows[0]?.id
    case 'portal_kind':
      return pick((k) => k.id === 'edge') ?? pick((k) => sem(k)['needs_anchor'] === false) ?? rows[0]?.id
    case 'path_kind':
      return pick((k) => sem(k)['locomotion'] === 'walk') ?? rows[0]?.id
    case 'anchor_kind':
      return digitKeys(rows)[0]?.kind.id ?? rows[0]?.id
  }
}

/* -------------------------------------------------------------------------- */
/* Picker grouping                                                             */
/* -------------------------------------------------------------------------- */

export interface KindGroup {
  id: string
  title: string
  kinds: KindDef[]
  /** Starts folded (the long "not set up yet" list). */
  collapsed?: boolean
}

type GroupRule = { id: string; title: string; test: (s: Record<string, unknown>) => boolean }

const has = (key: string, value: string) => (s: Record<string, unknown>) => Array.isArray(s[key]) && (s[key] as unknown[]).includes(value)

const GROUP_RULES: Record<StageDomain, GroupRule[]> = {
  region_kind: [
    { id: 'unset', title: 'Not set up yet', test: (s) => !Array.isArray(s['roles']) || (s['roles'] as unknown[]).length === 0 },
    { id: 'walk', title: 'Walk on', test: has('roles', 'walk') },
    { id: 'water', title: 'Water', test: has('roles', 'swim') },
    { id: 'air', title: 'Sky and air', test: has('roles', 'fly') },
    { id: 'block', title: 'Blocks the way', test: has('roles', 'block') },
    { id: 'hide', title: 'Hiding places', test: has('roles', 'hide') },
    { id: 'front', title: 'In front of characters', test: has('roles', 'occlude') },
    { id: 'back', title: 'Background', test: has('roles', 'backdrop') },
    { id: 'support', title: 'Put things on', test: has('roles', 'support') },
  ],
  anchor_kind: [
    { id: 'door', title: 'Go in and out', test: has('affords', 'enter') },
    { id: 'sleep', title: 'Sleep', test: has('affords', 'sleep') },
    { id: 'sit', title: 'Sit', test: (s) => has('affords', 'sit')(s) || has('affords', 'sit_at')(s) },
    { id: 'eat', title: 'Eat', test: has('affords', 'eat') },
    { id: 'hide', title: 'Hide and climb', test: (s) => has('affords', 'hide')(s) || has('affords', 'climb')(s) },
    { id: 'look', title: 'Look at', test: has('affords', 'look_out') },
    { id: 'place', title: 'Put things on', test: has('affords', 'place') },
    { id: 'marker', title: 'Start points and markers', test: (s) => !Array.isArray(s['affords']) || (s['affords'] as unknown[]).length === 0 },
  ],
  portal_kind: [
    { id: 'walk_off', title: 'Walk out of the picture', test: (s) => s['transition'] === 'walk_off' },
    { id: 'door_open', title: 'Doors', test: (s) => s['transition'] === 'door_open' },
    { id: 'fade', title: 'Fade away', test: (s) => s['transition'] === 'fade' },
    { id: 'cut', title: 'Jump cut', test: (s) => s['transition'] === 'cut' },
  ],
  path_kind: [
    { id: 'walk', title: 'Walk', test: (s) => s['locomotion'] === 'walk' },
    { id: 'fly', title: 'Fly', test: (s) => s['locomotion'] === 'fly' },
    { id: 'swim', title: 'Swim', test: (s) => s['locomotion'] === 'swim' },
    { id: 'climb', title: 'Climb', test: (s) => s['locomotion'] === 'climb' },
  ],
}

/** Text the picker's search matches: label, bare id, description, hint, and what it does. */
export function kindSearchText(kind: KindDef): string {
  return [kind.label['en'] ?? '', prettyId(kind.id), kind.id, kind.description ?? '', kind.ai_hint ?? '', ...describeKind(kind)].join(' ').toLowerCase()
}

/** Every search word must appear somewhere in the kind's text. */
export function matchesQuery(kind: KindDef, query: string): boolean {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean)
  if (words.length === 0) return true
  const text = kindSearchText(kind)
  return words.every((w) => text.includes(w))
}

/**
 * The picker's groups, in order: (while searching) Best match — the kinds whose name is or starts
 * with the search — then Pinned, Recent, On this picture, then one group per meaning
 * (each kind appears in exactly one of those), then "Other". Hidden (disabled) and replaced
 * (deprecated) kinds are left out — the picker lists them separately. With a query only matching
 * kinds remain, and nothing is folded.
 */
export function groupKinds(
  kinds: readonly KindDef[],
  opts: { domain: StageDomain; query?: string; pins?: readonly string[]; recent?: readonly string[]; counts?: ReadonlyMap<string, number> },
): KindGroup[] {
  const q = opts.query ?? ''
  const live = kinds.filter((k) => k.enabled && !k.deprecated && matchesQuery(k, q))
  const byId = new Map(live.map((k) => [k.id, k]))
  const pick = (ids: readonly string[] | undefined) => (ids ?? []).map((id) => byId.get(id)).filter((k): k is KindDef => Boolean(k))
  const groups: KindGroup[] = []
  // Searching: kinds whose NAME is what was typed come first, so "door" + Enter picks Door — not
  // Stairs, whose description merely mentions a door.
  const needle = q.trim().toLowerCase()
  if (needle) {
    const name = (k: KindDef) => (k.label['en'] ?? '').toLowerCase()
    const score = (k: KindDef): number => {
      const n = name(k)
      const bare = prettyId(k.id).toLowerCase()
      if (n === needle || bare === needle || k.id.toLowerCase() === needle) return 0
      if (n.startsWith(needle) || bare.startsWith(needle)) return 1
      return 2
    }
    const best = live
      .map((k) => ({ k, s: score(k) }))
      .filter((x) => x.s < 2)
      .sort((a, b) => a.s - b.s)
      .slice(0, 5)
      .map((x) => x.k)
    if (best.length) groups.push({ id: 'best', title: 'Best match', kinds: best })
  }
  const pinned = pick(opts.pins)
  if (pinned.length) groups.push({ id: 'pinned', title: 'Pinned', kinds: pinned })
  const recent = pick(opts.recent).filter((k) => !pinned.includes(k))
  if (recent.length) groups.push({ id: 'recent', title: 'Recent', kinds: recent })
  const here = live.filter((k) => (opts.counts?.get(k.id) ?? 0) > 0)
  if (here.length) groups.push({ id: 'here', title: 'On this picture', kinds: here })

  const rules = GROUP_RULES[opts.domain]
  const buckets = new Map<string, KindDef[]>(rules.map((r) => [r.id, []]))
  const other: KindDef[] = []
  for (const k of live) {
    const s = k.semantics as unknown as Record<string, unknown>
    const rule = rules.find((r) => r.test(s))
    if (rule) buckets.get(rule.id)!.push(k)
    else other.push(k)
  }
  const unsetLast = rules.filter((r) => r.id !== 'unset')
  const unset = rules.find((r) => r.id === 'unset')
  for (const r of unsetLast) {
    const list = buckets.get(r.id) ?? []
    if (list.length) groups.push({ id: r.id, title: r.title, kinds: list })
  }
  if (other.length) groups.push({ id: 'other', title: 'Other', kinds: other })
  if (unset) {
    const list = buckets.get('unset') ?? []
    if (list.length) groups.push({ id: 'unset', title: unset.title, kinds: list, collapsed: !q.trim() })
  }
  return groups
}

/* -------------------------------------------------------------------------- */
/* Registration                                                                */
/* -------------------------------------------------------------------------- */

/** Resolve anything shaped like a `VocabLike`, else the fallback. */
export function resolveVocab(candidate: unknown): VocabLike {
  const c = candidate as Partial<VocabLike> | null | undefined
  if (c && typeof c.byDomain === 'function' && typeof c.get === 'function') return c as VocabLike
  return fallbackVocab
}

let current: VocabLike = fallbackVocab
const listeners = new Set<() => void>()

/**
 * Tests (or a future replacement store) can register a vocabulary here; passing something that
 * is not a `VocabLike` is ignored rather than fatal.
 */
export function setVocabSource(source: unknown): void {
  const next = resolveVocab(source)
  if (next === current) return
  current = next
  for (const fn of listeners) fn()
}

export function getVocab(): VocabLike {
  return current
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

/* -------------------------------------------------------------------------- */
/* Adapter over the real store                                                 */
/* -------------------------------------------------------------------------- */

const adapterCache = new WeakMap<object, VocabLike>()

/** Wrap `vocabStore`'s shape in the methods the annotator needs. */
export function adaptVocabStore(state: VocabState): VocabLike {
  const cached = adapterCache.get(state)
  if (cached) return cached
  const adapter: VocabLike = {
    rev: state.rev,
    byDomain: (domain) => kindsOf(state, domain),
    get: (id, domain) => {
      if (domain) return resolveKind(state, domain, id) ?? undefined
      const order: Domain[] = [...STAGE_DOMAINS, ...(Object.keys(state.domains) as Domain[]).filter((d) => !isStageDomain(d))]
      for (const d of order) {
        const hit = resolveKind(state, d, id)
        if (hit) return hit
      }
      return undefined
    },
  }
  adapterCache.set(state, adapter)
  return adapter
}

/**
 * React binding. Prefers the live vocabulary store, then anything registered through
 * `setVocabSource`, then the built-in fallback — so the annotator renders correct colours on the
 * first frame and upgrades silently when `GET /api/vocab` lands.
 */
export function useVocab(): VocabLike {
  const state = useVocabStore()
  const registered = useSyncExternalStore(subscribe, getVocab, getVocab)
  return useMemo(() => (state.loaded ? adaptVocabStore(state) : registered), [state, registered])
}

/** The vocabulary right now, outside React (after a mutation has just written a new kind). */
export function currentVocab(): VocabLike {
  const state = useVocabStore.getState()
  return state.loaded ? adaptVocabStore(state) : getVocab()
}

/** True while the annotator is running on built-in kinds only. */
export function isFallbackVocab(v: VocabLike): boolean {
  return v === fallbackVocab
}

/* -------------------------------------------------------------------------- */
/* Kind edits shared by the kind bar, the details panel and the layer list      */
/* -------------------------------------------------------------------------- */

type EditorApi = ReturnType<typeof useEditorStore.getState> & {
  /** The save/undo track's single edit path, when it exists: one undo step, merged by `coalesceKey`. */
  commit?: (fn: (d: EditorDoc) => void, opts?: { coalesceKey?: string }) => void
}

/** One undoable edit of the document (merged with the previous one when `coalesceKey` matches). */
export function editDoc(fn: (d: EditorDoc) => void, opts: { coalesceKey?: string } = {}): void {
  const s = useEditorStore.getState() as EditorApi
  if (typeof s.commit === 'function') {
    s.commit(fn, opts)
    return
  }
  s.beginGesture()
  s.patchDoc(fn)
  s.endGesture()
}

/**
 * Give `ids` the kind `kind` — but only the ones of `domain`: pressing `1` with a spot selected
 * must never turn it into "sky". Returns how many features changed.
 */
export function setKindOf(ids: readonly string[], kind: string, domain: StageDomain): number {
  const doc = useEditorStore.getState().doc
  const targets = featuresOf(doc, domain).filter((f) => ids.includes(f.id) && f.kind !== kind)
  if (targets.length === 0) return 0
  const want = new Set(targets.map((f) => f.id))
  editDoc((d) => {
    for (const f of featuresOf(d, domain)) {
      if (!want.has(f.id)) continue
      f.kind = kind
      f.provenance = humanProvenance()
    }
  })
  return targets.length
}

/** Deep-enough equality for the values the panels write (scalars and small point arrays). */
function sameValue(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (Array.isArray(a) && Array.isArray(b)) return a.length === b.length && a.every((x, i) => sameValue(x, b[i]))
  return false
}

/**
 * Write one per-kind field value into the feature's `props` (empty / `undefined` removes it).
 * Writing the value it already has changes nothing — no undo step, no "Unsaved changes".
 */
export function setFeatureProp(id: string, key: string, value: string | number | boolean | undefined): void {
  const clean = value === '' || (typeof value === 'number' && !Number.isFinite(value)) ? undefined : value
  editDoc(
    (d) => {
      if (!isStageBody(d)) return
      for (const domain of STAGE_DOMAINS) {
        const f = featuresOf(d, domain).find((x) => x.id === id)
        if (!f) continue
        const had = f.props?.[key]
        if (clean === undefined ? had === undefined : had === clean) return
        const props = { ...(f.props ?? {}) }
        if (clean === undefined) delete props[key]
        else props[key] = clean
        if (Object.keys(props).length) f.props = props
        else delete f.props
        f.provenance = humanProvenance()
        return
      }
    },
    { coalesceKey: `prop:${id}:${key}` },
  )
}

/**
 * Set several top-level fields of one feature at once (`undefined` removes a field). Fields that
 * already hold the value are left alone, and when nothing changes nothing is recorded: pressing
 * the option that is already on is not an edit.
 */
export function setFeatureFields(id: string, fields: Record<string, unknown>, coalesceKey?: string): void {
  editDoc(
    (d) => {
      if (!isStageBody(d)) return
      for (const domain of STAGE_DOMAINS) {
        const f = featuresOf(d, domain).find((x) => x.id === id) as unknown as Record<string, unknown> | undefined
        if (!f) continue
        let changed = false
        for (const [k, v] of Object.entries(fields)) {
          if (v === undefined) {
            if (!(k in f)) continue
            delete f[k]
          } else {
            if (sameValue(f[k], v)) continue
            f[k] = v
          }
          changed = true
        }
        if (changed) f['provenance'] = humanProvenance()
        return
      }
    },
    coalesceKey ? { coalesceKey } : {},
  )
}

export const PORTAL_EDGES: readonly PortalEdge[] = ['left', 'right', 'bottom', 'top']

/**
 * Add a way in or out of `kind`: on the spot `anchorId` when given, else on the first picture
 * edge no other way uses yet. Selects it and returns its id.
 */
export function addPortal(kind: string, where: { anchorId?: string; edge?: PortalEdge } = {}): string | null {
  const doc = useEditorStore.getState().doc
  if (!isStageBody(doc)) return null
  const id = `p_${nanoid(8)}`
  const used = new Set(doc.portals.map((p) => p.edge).filter(Boolean))
  const edge = where.anchorId ? undefined : (where.edge ?? PORTAL_EDGES.find((e) => !used.has(e)) ?? 'left')
  editDoc((d) => {
    if (!isStageBody(d)) return
    d.portals.push({
      id,
      kind,
      ...(where.anchorId ? { anchor_id: where.anchorId } : { edge }),
      provenance: humanProvenance(),
    })
  })
  useEditorStore.getState().select([id])
  return id
}
